/**
 * Category Utilities
 * 
 * CRITICAL RULE: Category is a FIRST-CLASS ENTITY.
 * Always render category.name_th or category.name_en.
 * NEVER render the raw category object.
 */

/**
 * Safely get category display name
 * @param {Object} category - Category object
 * @param {string} preferredLanguage - 'th' or 'en', defaults to 'th'
 * @returns {string} - Safe string representation
 */
export const getCategoryName = (category, preferredLanguage = 'th') => {
  if (!category || typeof category !== 'object') {
    return 'Unknown Category';
  }
  
  // NEVER return the object itself
  if (Array.isArray(category)) {
    return 'Invalid Category';
  }
  
  // Check for category object indicators (should not happen if filtering works)
  if (category.name_en !== undefined || category.name_th !== undefined) {
    if (preferredLanguage === 'th' && category.name_th) {
      return category.name_th;
    }
    if (category.name_en) {
      return category.name_en;
    }
    if (category.name_th) {
      return category.name_th;
    }
  }
  
  // Fallback: if it's not a category object, return safe string
  return category.title || category.title_th || String(category.id || 'Unknown');
};

/**
 * Safely render category name in JSX
 * Always returns a string, never an object
 */
export const CategoryName = ({ category, preferredLanguage = 'th', className = '' }) => {
  const name = getCategoryName(category, preferredLanguage);
  return <span className={className}>{name}</span>;
};

/**
 * Check if an object is a category object
 * @param {*} obj - Object to check
 * @returns {boolean}
 */
export const isCategoryObject = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return false;
  }
  
  // Category objects have these specific fields
  return (
    obj.name_en !== undefined ||
    obj.name_th !== undefined ||
    obj.has_bonuses !== undefined ||
    obj.bonuses !== undefined ||
    obj.default_start_time !== undefined ||
    obj.is_weekly !== undefined ||
    obj.day_of_week !== undefined ||
    obj.is_monthly !== undefined ||
    obj.is_multi_day !== undefined
  );
};

/**
 * Filter out category objects from an array
 * @param {Array} items - Array of items
 * @returns {Array} - Filtered array without category objects
 */
export const filterOutCategoryObjects = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }
  
  return items.filter(item => !isCategoryObject(item));
};
